import { getBearerToken } from "../../../../api/postCall/bearerToken";
import {
  searchForMedicalPaOrdersFromPostgres,
  SearchForMedicalPaOrdersFromPostgresParams,
} from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import {
  Hit,
  PostgresResponseForMedicalPaOrders,
} from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { GenericAuthStatusTableData } from "../../../../data-model/tablesData";
import { GenericAuthStatusState } from "../hooks/useGenericAuthStatusTableData";
import { generateGenericAuthStatusTableDataFromAlgoliaRecords } from "../utils/generateTableDataFromAlgoliaRecords";

/**
 * Fetches the next page of data and appends it to existing table state.
 * Shows an inline loader during the request instead of a full-page loader.
 *
 * @param payload - Search parameters for the next page Postgres query
 * @param authStatusOptions - Auth status options for formatting table badge data
 * @param setGenericAuthStatusState - State setter for API response metadata
 * @param setTableData - State setter that appends new rows to existing table data
 */
export const makePostCallForNextPagesDataFetch = async (
  payload: SearchForMedicalPaOrdersFromPostgresParams,
  authStatusOptions: AuthStatusOptionModel[],
  setGenericAuthStatusState: React.Dispatch<
    React.SetStateAction<GenericAuthStatusState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<GenericAuthStatusTableData[]>
  >,
) => {
  setGenericAuthStatusState((prev) => ({ ...prev, showinLineLoader: true }));
  try {
    await getBearerToken();
    const response = await searchForMedicalPaOrdersFromPostgres(payload);
    const data = JSON.parse(response) as PostgresResponseForMedicalPaOrders;
    setGenericAuthStatusState((prev) => ({
      ...prev,
      data: {
        ...data,
        hits: appendHitsToExistingTableData(
          prev.data?.hits ?? [],
          data?.hits ?? [],
        ),
      },
    }));
    setTableData((prev) => [
      ...prev,
      ...generateGenericAuthStatusTableDataFromAlgoliaRecords(
        data.hits ?? [],
        authStatusOptions,
      ),
    ]);
  } catch (error) {
    setGenericAuthStatusState((prev) => ({ ...prev, error: error as string }));
  } finally {
    setGenericAuthStatusState((prev) => ({ ...prev, showinLineLoader: false }));
  }
};

const appendHitsToExistingTableData = (
  existingTableData: Hit[],
  newHits: Hit[],
) => {
  return existingTableData.concat(newHits);
};
